import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fileUrl, formatApiError, formatCurrency } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Plus, Trash2, Printer, CreditCard, Send, ImageOff, Search, X, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function WaiterTableOrder() {
  const { tableId } = useParams();
  const [params] = useSearchParams();
  const orderId = params.get("orderId");
  const navigate = useNavigate();
  const { user } = useAuth();

  const [order, setOrder] = useState(null);
  const [dishes, setDishes] = useState([]);
  const [search, setSearch] = useState("");
  const [openAdd, setOpenAdd] = useState(false);
  const [openCheckout, setOpenCheckout] = useState(false);
  const [checkoutMode, setCheckoutMode] = useState("equal"); // "equal" | "items"
  const [itemSplits, setItemSplits] = useState([{ label: "Persona 1", item_ids: [] }, { label: "Persona 2", item_ids: [] }]);
  const [selectedDish, setSelectedDish] = useState(null);
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState("");
  const [selectedMods, setSelectedMods] = useState([]);
  const [splitCount, setSplitCount] = useState(1);

  const loadOrder = useCallback(async () => {
    if (!orderId) return;
    try { const { data } = await api.get(`/orders/${orderId}`); setOrder(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  }, [orderId]);

  useEffect(() => {
    loadOrder();
    (async () => {
      try { const { data } = await api.get("/dishes"); setDishes(data); }
      catch (err) { console.error("dishes load failed:", err); }
    })();
  }, [orderId, loadOrder]);

  const otherWaiter = order && order.waiter_id !== user.id;

  const addItem = async () => {
    if (!selectedDish) return;
    try {
      const { data } = await api.post(`/orders/${order.id}/items`, {
        dish_id: selectedDish.id, quantity: qty, notes, modifiers: selectedMods,
      });
      setOrder(data);
      toast.success(`${selectedDish.name} agregado`);
      setSelectedDish(null); setQty(1); setNotes(""); setSelectedMods([]);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleMod = (mod, group) => {
    setSelectedMods(prev => {
      const exists = prev.find(m => m.id === mod.id);
      // For single-choice groups: replace any selection within the same group
      if (group && group.type === "single") {
        const otherIds = new Set((group.options || []).map(o => o.id));
        const cleaned = prev.filter(m => !otherIds.has(m.id));
        return exists ? cleaned : [...cleaned, { id: mod.id, name: mod.name, price_delta: mod.price_delta, group_id: group.id }];
      }
      if (exists) return prev.filter(m => m.id !== mod.id);
      return [...prev, { id: mod.id, name: mod.name, price_delta: mod.price_delta, group_id: group ? group.id : null }];
    });
  };

  const dishUnitPrice = (dish, mods) => {
    const delta = (mods || []).reduce((acc, m) => acc + Number(m.price_delta || 0), 0);
    return Number(dish?.price || 0) + delta;
  };

  const itemUnitPrice = (it) => {
    const delta = (it.modifiers || []).reduce((acc, m) => acc + Number(m.price_delta || 0), 0);
    return Number(it.price) + delta;
  };

  // Check that all required modifier groups have a selection
  const missingRequiredGroups = (dish) => {
    return (dish?.modifier_groups || []).filter(g => g.required && !selectedMods.find(m => m.group_id === g.id));
  };

  const removeItem = async (itemId) => {
    try {
      const { data } = await api.delete(`/orders/${order.id}/items/${itemId}`);
      setOrder(data);
      toast.success("Platillo removido");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const confirmAndPrint = async () => {
    try {
      await api.post(`/orders/${order.id}/confirm`);
      toast.success("Pedido enviado a cocina");
      window.open(`/print/${order.id}?type=kitchen`, "_blank");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const checkout = async () => {
    try {
      const sc = Math.max(1, Math.min(20, parseInt(splitCount, 10) || 1));
      const { data } = await api.post(`/orders/${order.id}/checkout`, { payment_method: "cash", split_count: sc });
      setOrder(data);
      toast.success(sc > 1 ? `Mesa cobrada — dividida en ${sc}` : "Mesa cobrada y cerrada");
      window.open(`/print/${data.id}?type=receipt`, "_blank");
      setTimeout(() => navigate("/waiter"), 600);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const checkoutByItems = async () => {
    // Validate all items assigned
    const assigned = new Set();
    for (const s of itemSplits) for (const iid of s.item_ids) assigned.add(iid);
    const unassigned = order.items.filter(it => !assigned.has(it.id));
    if (unassigned.length > 0) {
      toast.error(`Hay ${unassigned.length} platillo(s) sin asignar`);
      return;
    }
    const nonEmpty = itemSplits.filter(s => s.item_ids.length > 0);
    if (nonEmpty.length === 0) {
      toast.error("Asigna platillos a las cuentas");
      return;
    }
    try {
      const { data } = await api.post(`/orders/${order.id}/checkout-by-items`, {
        splits: nonEmpty.map(s => ({ label: s.label, item_ids: s.item_ids })),
        payment_method: "cash",
      });
      setOrder(data);
      toast.success(`Mesa cobrada — ${nonEmpty.length} cuentas`);
      window.open(`/print/${data.id}?type=receipt`, "_blank");
      setTimeout(() => navigate("/waiter"), 600);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const moveItemToSplit = (itemId, splitIndex) => {
    setItemSplits(prev => prev.map((s, i) => ({
      ...s,
      item_ids: s.item_ids.filter(iid => iid !== itemId).concat(i === splitIndex ? [itemId] : []),
    })));
  };
  const unassignItem = (itemId) => {
    setItemSplits(prev => prev.map(s => ({ ...s, item_ids: s.item_ids.filter(iid => iid !== itemId) })));
  };
  const addSplitBucket = () => {
    setItemSplits(prev => [...prev, { label: `Persona ${prev.length + 1}`, item_ids: [] }]);
  };
  const removeSplitBucket = (idx) => {
    setItemSplits(prev => prev.filter((_, i) => i !== idx));
  };
  const renameSplitBucket = (idx, label) => {
    setItemSplits(prev => prev.map((s, i) => i === idx ? { ...s, label } : s));
  };

  const splitSubtotal = (split) => {
    return split.item_ids.reduce((acc, iid) => {
      const it = order.items.find(x => x.id === iid);
      return it ? acc + itemUnitPrice(it) * it.quantity : acc;
    }, 0);
  };
  const assignedSet = new Set(itemSplits.flatMap(s => s.item_ids));

  if (!order) return <p className="text-[#7248ae]">Cargando pedido…</p>;

  const filtered = dishes.filter(d =>
    d.name.toLowerCase().includes(search.toLowerCase()) ||
    (d.category || "").toLowerCase().includes(search.toLowerCase()),
  );
  const grouped = filtered.reduce((acc, d) => {
    const k = d.category || "General";
    if (!acc[k]) acc[k] = [];
    acc[k].push(d);
    return acc;
  }, {});

  return (
    <div className="animate-fade-up pb-4" data-testid="waiter-order-page">
      <Link to="/waiter" className="inline-flex items-center text-sm text-[#7248ae] hover:text-[#3e166c] mb-4">
        <ArrowLeft className="h-4 w-4 mr-2" />Mesas
      </Link>

      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#7248ae]">Mesa</p>
          <h1 className="font-display text-5xl font-black text-[#1a0f2e]">{order.table_number}</h1>
          <p className="text-sm text-[#7248ae]">Atiende: <strong className="text-[#1a0f2e]">{order.waiter_name}</strong></p>
        </div>
        <Badge className={order.status === "paid" ? "bg-[#10b981]" : "bg-[#3e166c]"}>
          {order.status === "paid" ? "Pagada" : "Abierta"}
        </Badge>
      </div>

      {otherWaiter && (
        <Card className="p-4 mb-4 bg-[#f59e0b]/10 border-[#f59e0b]/30">
          <p className="text-sm text-[#1a0f2e]">Esta mesa la atiende otro mesero. Solo puedes ver.</p>
        </Card>
      )}

      <Card className="p-5 border border-[#b182ec]/30 rounded-2xl mb-4" data-testid="order-summary">
        <ul className="space-y-2 mb-4" data-testid="order-items-list">
          {order.items.length === 0 && (
            <p className="text-[#7248ae] text-sm py-4 text-center">Agrega el primer platillo del pedido.</p>
          )}
          {order.items.map(it => (
            <li key={it.id} className="flex items-start justify-between gap-3 p-3 rounded-xl bg-[#fcfbfd] border border-[#b182ec]/20" data-testid={`order-item-${it.name}`}>
              <div className="flex-1">
                <p className="text-[#1a0f2e]"><strong>{it.quantity}×</strong> {it.name}</p>
                {(it.modifiers || []).length > 0 && (
                  <p className="text-xs text-[#3e166c] mt-0.5">
                    + {(it.modifiers || []).map(m => m.price_delta > 0 ? `${m.name} (+${formatCurrency(m.price_delta)})` : m.name).join(", ")}
                  </p>
                )}
                {it.notes && <p className="text-xs text-[#7248ae] italic mt-1">📝 {it.notes}</p>}
              </div>
              <div className="text-right">
                <p className="text-[#3e166c] font-semibold">{formatCurrency(itemUnitPrice(it) * it.quantity)}</p>
                {!otherWaiter && order.status === "open" && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-[#e11d48] hover:bg-[#e11d48]/10" data-testid={`remove-item-${it.name}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>¿Quitar &quot;{it.name}&quot;?</AlertDialogTitle>
                        <AlertDialogDescription>Esto removerá el platillo del pedido.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction className="bg-[#e11d48] hover:bg-[#e11d48]/80" onClick={() => removeItem(it.id)} data-testid={`confirm-remove-${it.name}`}>Quitar</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </li>
          ))}
        </ul>
        <div className="border-t border-[#b182ec]/30 pt-3 flex items-center justify-between">
          <span className="text-xs uppercase tracking-wider text-[#7248ae]">Total</span>
          <span className="font-display text-3xl font-black text-[#3e166c]" data-testid="order-total">{formatCurrency(order.total)}</span>
        </div>
      </Card>

      {!otherWaiter && order.status === "open" && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <Sheet open={openAdd} onOpenChange={setOpenAdd}>
            <SheetTrigger asChild>
              <Button className="h-12 bg-[#3e166c] hover:bg-[#7248ae] text-white rounded-xl" data-testid="add-dish-button">
                <Plus className="h-5 w-5 mr-2" />Agregar
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="h-[88vh] overflow-y-auto rounded-t-3xl">
              <SheetHeader>
                <SheetTitle className="font-display text-2xl text-[#1a0f2e]">{selectedDish ? "Confirmar platillo" : "Elige un platillo"}</SheetTitle>
              </SheetHeader>

              {selectedDish ? (
                <div className="mt-4" data-testid="dish-confirm-panel">
                  <div className="flex gap-4 mb-4">
                    <div className="w-24 h-24 rounded-xl overflow-hidden bg-[#fcfbfd] border border-[#b182ec]/30 flex items-center justify-center">
                      {selectedDish.photo_url ? <img src={fileUrl(selectedDish.photo_url)} alt="" className="w-full h-full object-cover" /> : <ImageOff className="h-7 w-7 text-[#b182ec]" />}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-display text-xl font-bold text-[#1a0f2e]">{selectedDish.name}</h3>
                      <p className="text-sm text-[#7248ae]">{selectedDish.description}</p>
                      <p className="font-display text-lg font-bold text-[#3e166c] mt-1">{formatCurrency(dishUnitPrice(selectedDish, selectedMods))}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {(selectedDish.modifiers || []).length > 0 && (
                      <div data-testid="modifiers-picker">
                        <Label className="text-xs uppercase tracking-wider text-[#7248ae] mb-2 block">Extras</Label>
                        <div className="grid grid-cols-1 gap-2">
                          {selectedDish.modifiers.map(mod => {
                            const active = !!selectedMods.find(m => m.id === mod.id);
                            return (
                              <button
                                type="button"
                                key={mod.id}
                                onClick={() => toggleMod(mod, null)}
                                className={`flex items-center justify-between p-3 rounded-xl border text-sm transition-colors text-left ${active ? "bg-[#3e166c] text-white border-[#3e166c]" : "bg-white text-[#1a0f2e] border-[#b182ec]/40 hover:border-[#3e166c]"}`}
                                data-testid={`modifier-toggle-${mod.name}`}
                              >
                                <span>{mod.name}</span>
                                <span className={`text-xs ${active ? "text-white/80" : "text-[#7248ae]"}`}>
                                  {Number(mod.price_delta) > 0 ? `+${formatCurrency(mod.price_delta)}` : Number(mod.price_delta) < 0 ? formatCurrency(mod.price_delta) : "Sin costo"}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {(selectedDish.modifier_groups || []).map(group => (
                      <div key={group.id} data-testid={`group-picker-${group.name}`}>
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-xs uppercase tracking-wider text-[#7248ae]">
                            {group.name} {group.required && <span className="text-[#e11d48]">*</span>}
                          </Label>
                          <span className="text-[10px] uppercase tracking-wider text-[#7248ae]">
                            {group.type === "single" ? "Elige uno" : "Varios"}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 gap-2">
                          {(group.options || []).map(opt => {
                            const active = !!selectedMods.find(m => m.id === opt.id);
                            return (
                              <button
                                type="button"
                                key={opt.id}
                                onClick={() => toggleMod(opt, group)}
                                className={`flex items-center justify-between p-3 rounded-xl border text-sm transition-colors text-left ${active ? "bg-[#3e166c] text-white border-[#3e166c]" : "bg-white text-[#1a0f2e] border-[#b182ec]/40 hover:border-[#3e166c]"}`}
                                data-testid={`group-option-${group.name}-${opt.name}`}
                              >
                                <span className="flex items-center gap-2">
                                  <span className={`w-4 h-4 ${group.type === "single" ? "rounded-full" : "rounded"} border-2 ${active ? "bg-white border-white" : "border-[#b182ec]"}`} />
                                  {opt.name}
                                </span>
                                <span className={`text-xs ${active ? "text-white/80" : "text-[#7248ae]"}`}>
                                  {Number(opt.price_delta) > 0 ? `+${formatCurrency(opt.price_delta)}` : Number(opt.price_delta) < 0 ? formatCurrency(opt.price_delta) : ""}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-[#7248ae] mb-2 block">Cantidad</Label>
                      <div className="flex items-center gap-3">
                        <Button type="button" variant="outline" onClick={() => setQty(Math.max(1, qty-1))} className="h-12 w-12 border-[#b182ec]/50" data-testid="qty-minus">−</Button>
                        <Input type="number" min={1} value={qty} onChange={e=>setQty(parseInt(e.target.value || "1"))} className="h-12 text-center text-lg" data-testid="qty-input" />
                        <Button type="button" variant="outline" onClick={() => setQty(qty+1)} className="h-12 w-12 border-[#b182ec]/50" data-testid="qty-plus">+</Button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-[#7248ae] mb-2 block">Notas (ej: sin cebolla)</Label>
                      <Textarea rows={2} value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Sin cebolla, término medio…" data-testid="dish-notes-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-3 pt-2">
                      <Button variant="outline" onClick={() => { setSelectedDish(null); setSelectedMods([]); }} className="h-12 border-[#b182ec]/50" data-testid="back-to-menu-button">Volver</Button>
                      <Button onClick={async () => { await addItem(); setOpenAdd(false); }} className="h-12 bg-[#3e166c] hover:bg-[#7248ae] text-white" data-testid="confirm-add-button">
                        Agregar al pedido
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-4">
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7248ae]" />
                    <Input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar platillo o categoría…" className="pl-9 border-[#b182ec]/50" data-testid="dish-search-input" />
                  </div>
                  {Object.keys(grouped).length === 0 && <p className="text-[#7248ae] text-sm">Sin resultados.</p>}
                  {Object.entries(grouped).map(([cat, items]) => (
                    <div key={cat} className="mb-5">
                      <p className="text-xs uppercase tracking-[0.2em] text-[#7248ae] font-semibold mb-2">{cat}</p>
                      <div className="grid grid-cols-1 gap-2">
                        {items.map(d => (
                          <button
                            key={d.id}
                            onClick={() => setSelectedDish(d)}
                            className="flex items-center gap-3 p-2 rounded-xl border border-[#b182ec]/30 hover:border-[#3e166c] hover:bg-[#3e166c]/5 text-left transition-colors"
                            data-testid={`menu-dish-${d.name}`}
                          >
                            <div className="w-14 h-14 rounded-lg overflow-hidden bg-[#fcfbfd] border border-[#b182ec]/30 flex items-center justify-center">
                              {d.photo_url ? <img src={fileUrl(d.photo_url)} alt="" className="w-full h-full object-cover" /> : <ImageOff className="h-5 w-5 text-[#b182ec]" />}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-semibold text-[#1a0f2e]">{d.name}</p>
                              <p className="text-xs text-[#7248ae] line-clamp-1">{d.description}</p>
                            </div>
                            <span className="font-display text-base font-bold text-[#3e166c]">{formatCurrency(d.price)}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SheetContent>
          </Sheet>

          <Button
            onClick={confirmAndPrint}
            disabled={order.items.length === 0}
            className="h-12 bg-[#7248ae] hover:bg-[#3e166c] text-white rounded-xl"
            data-testid="confirm-send-button"
          >
            <Send className="h-5 w-5 mr-2" />Confirmar e imprimir
          </Button>

          <Dialog open={openCheckout} onOpenChange={setOpenCheckout}>
            <DialogTrigger asChild>
              <Button disabled={order.items.length === 0} className="h-12 bg-[#10b981] hover:bg-[#10b981]/80 text-white rounded-xl" data-testid="checkout-button">
                <CreditCard className="h-5 w-5 mr-2" />Cobrar
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Cobrar mesa {order.table_number}</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-[#7248ae]">Total: <strong className="text-[#3e166c] font-display text-lg">{formatCurrency(order.total)}</strong></p>
              <Tabs value={checkoutMode} onValueChange={setCheckoutMode} className="mt-4">
                <TabsList className="grid grid-cols-2 w-full">
                  <TabsTrigger value="equal" data-testid="tab-split-equal">División equitativa</TabsTrigger>
                  <TabsTrigger value="items" data-testid="tab-split-items">Por items</TabsTrigger>
                </TabsList>
                <TabsContent value="equal" className="pt-4">
                  <Label className="text-xs uppercase tracking-wider text-[#7248ae] mb-2 block">Dividir entre</Label>
                  <div className="flex items-center gap-3">
                    <Button type="button" variant="outline" onClick={() => setSplitCount(Math.max(1, splitCount-1))} className="h-10 w-10 border-[#b182ec]/50" data-testid="split-minus">−</Button>
                    <Input
                      type="number" min={1} max={20}
                      value={splitCount}
                      onChange={e => setSplitCount(parseInt(e.target.value || "1"))}
                      className="h-10 w-20 text-center"
                      data-testid="split-input"
                    />
                    <Button type="button" variant="outline" onClick={() => setSplitCount(Math.min(20, splitCount+1))} className="h-10 w-10 border-[#b182ec]/50" data-testid="split-plus">+</Button>
                    <span className="text-sm text-[#7248ae]">
                      {splitCount === 1 ? "Una sola cuenta" : `${formatCurrency(order.total / splitCount)} por persona`}
                    </span>
                  </div>
                  <DialogFooter className="mt-6">
                    <Button onClick={() => setOpenCheckout(false)} variant="outline">Cancelar</Button>
                    <Button onClick={checkout} className="bg-[#10b981] hover:bg-[#10b981]/80 text-white" data-testid="confirm-checkout-button">
                      Cobrar e imprimir ticket
                    </Button>
                  </DialogFooter>
                </TabsContent>
                <TabsContent value="items" className="pt-4 space-y-4">
                  <p className="text-sm text-[#7248ae]">Asigna cada platillo a una persona. Cada cuenta se imprime por separado.</p>
                  <div className="space-y-3" data-testid="splits-list">
                    {itemSplits.map((s, idx) => (
                      <div key={idx} className="p-3 rounded-xl border border-[#b182ec]/30 bg-[#fcfbfd]" data-testid={`split-bucket-${idx}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Users className="h-4 w-4 text-[#3e166c]" />
                          <Input
                            value={s.label}
                            onChange={e => renameSplitBucket(idx, e.target.value)}
                            className="h-7 text-sm flex-1"
                            data-testid={`split-label-${idx}`}
                          />
                          <span className="font-display font-bold text-[#3e166c]">{formatCurrency(splitSubtotal(s))}</span>
                          <Button type="button" size="icon" variant="ghost" onClick={() => removeSplitBucket(idx)} disabled={itemSplits.length <= 1} className="h-7 w-7 text-[#e11d48]" data-testid={`remove-split-${idx}`}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                        <div className="space-y-1.5 min-h-[40px]">
                          {s.item_ids.length === 0 && <p className="text-xs text-[#7248ae] italic">Sin platillos asignados.</p>}
                          {s.item_ids.map(iid => {
                            const it = order.items.find(x => x.id === iid);
                            if (!it) return null;
                            return (
                              <div key={iid} className="flex items-center justify-between text-sm bg-white p-2 rounded-lg border border-[#b182ec]/20">
                                <span>{it.quantity}× {it.name}</span>
                                <button type="button" onClick={() => unassignItem(iid)} className="text-xs text-[#e11d48]" data-testid={`unassign-${iid}`}>Quitar</button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    <Button type="button" variant="outline" onClick={addSplitBucket} className="w-full border-dashed border-[#b182ec]/50" data-testid="add-split-bucket">
                      <Plus className="h-4 w-4 mr-2" />Agregar persona
                    </Button>
                  </div>

                  {order.items.filter(it => !assignedSet.has(it.id)).length > 0 && (
                    <div data-testid="unassigned-items">
                      <Label className="text-xs uppercase tracking-wider text-[#7248ae] mb-2 block">Platillos sin asignar</Label>
                      <div className="space-y-1.5">
                        {order.items.filter(it => !assignedSet.has(it.id)).map(it => (
                          <div key={it.id} className="flex items-center gap-2 p-2 rounded-lg bg-[#fcfbfd] border border-[#b182ec]/20 text-sm">
                            <span className="flex-1">{it.quantity}× {it.name} — {formatCurrency(itemUnitPrice(it) * it.quantity)}</span>
                            <select
                              className="h-8 px-2 rounded-md border border-[#b182ec]/40 bg-white text-sm"
                              defaultValue=""
                              onChange={e => { if (e.target.value !== "") moveItemToSplit(it.id, Number(e.target.value)); e.target.value=""; }}
                              data-testid={`assign-select-${it.id}`}
                            >
                              <option value="" disabled>Asignar a…</option>
                              {itemSplits.map((s, i) => (
                                <option key={i} value={i}>{s.label}</option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <DialogFooter>
                    <Button onClick={() => setOpenCheckout(false)} variant="outline">Cancelar</Button>
                    <Button onClick={checkoutByItems} className="bg-[#10b981] hover:bg-[#10b981]/80 text-white" data-testid="confirm-checkout-by-items-button">
                      Cobrar e imprimir tickets
                    </Button>
                  </DialogFooter>
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {order.status === "paid" && (
        <Button onClick={() => window.open(`/print/${order.id}?type=receipt`, "_blank")} variant="outline" className="w-full h-12 border-[#b182ec]/50" data-testid="reprint-button">
          <Printer className="h-5 w-5 mr-2" />Reimprimir ticket
        </Button>
      )}
    </div>
  );
}
